import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { buildSagelMeetingUrl, resolveMeetingUrl, SupportedMeetingLanguage } from '../meetings/meeting-link.util';

const API_BASE = `${environment.baseUrl}/api/v1/general-appointments`;
const SUPPORTED_LANGS = ['en', 'tr', 'es', 'el', 'de'];
const SLOT_DURATION_MIN = 60;
const SLOTS_START_H = 8;
const SLOTS_END_H = 20;

interface CalDay {
  date: Date;
  isCurrentMonth: boolean;
  isPast: boolean;
  isToday: boolean;
}

@Component({
    selector: 'app-book-appointment',
    templateUrl: './book-appointment.component.html',
    styleUrls: ['./book-appointment.component.scss'],
    standalone: false
})
export class BookAppointmentComponent implements OnInit {

  companyName = '';
  phase: 'loading' | 'pick' | 'details' | 'success' | 'error' = 'loading';
  errorText = '';

  // ── Calendar ──────────────────────────────────────────────────────────────
  currentMonth = new Date();
  selectedDate: Date | null = null;
  selectedSlot: string | null = null; // 'HH:mm'

  // ── Details form ──────────────────────────────────────────────────────────
  name = '';
  email = '';
  phone = '';
  notes = '';
  formError = '';
  isSubmitting = false;

  // ── Timezone ──────────────────────────────────────────────────────────────
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  showTzPicker = false;
  tzSearch = '';

  readonly allTimezones: string[] = (() => {
    try {
      return (Intl as any).supportedValuesOf('timeZone') as string[];
    } catch {
      return [
        'Pacific/Honolulu','America/Anchorage','America/Los_Angeles','America/Denver',
        'America/Chicago','America/New_York','America/Sao_Paulo','Atlantic/Azores',
        'Europe/London','Europe/Lisbon','Europe/Berlin','Europe/Paris','Europe/Rome',
        'Europe/Istanbul','Europe/Helsinki','Asia/Dubai','Asia/Kolkata','Asia/Dhaka',
        'Asia/Bangkok','Asia/Singapore','Asia/Tokyo','Asia/Seoul','Australia/Sydney',
        'Pacific/Auckland',
      ];
    }
  })();

  get filteredTimezones(): string[] {
    const q = this.tzSearch.toLowerCase();
    return q ? this.allTimezones.filter(tz => tz.toLowerCase().includes(q)) : this.allTimezones;
  }

  selectTimezone(tz: string): void {
    this.timezone = tz;
    this.showTzPicker = false;
    this.tzSearch = '';
  }

  private userId = '';
  private appointments: any[] = [];
  private lang: SupportedMeetingLanguage = 'en';

  get weekDayLabels(): string[] {
    return ['MON','TUE','WED','THU','FRI','SAT','SUN']
      .map(k => this.translate.instant(`BOOK_APPOINTMENT.${k}`));
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
  ) {
    const browserLang = navigator.language.split('-')[0];
    this.lang = (SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'en') as SupportedMeetingLanguage;
    this.translate.use(this.lang);
    this.currentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const company = params.get('companyName');
      if (company) {
        this.companyName = decodeURIComponent(company);
        this.loadCompany();
      } else {
        this.phase = 'error';
        this.errorText = this.translate.instant('BOOK_APPOINTMENT.ERROR_INVALID_LINK');
      }
    });
  }

  private loadCompany(): void {
    this.phase = 'loading';
    this.http
      .get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
        params: { company_name: this.companyName },
      })
      .subscribe({
        next: res => {
          this.userId = res.user_id;
          this.loadAppointments();
        },
        error: () => {
          this.phase = 'error';
          this.errorText = this.translate.instant('BOOK_APPOINTMENT.ERROR_COMPANY_NOT_FOUND');
        },
      });
  }

  private loadAppointments(): void {
    this.http
      .get<any>(`${API_BASE}/public/user/${this.userId}`)
      .subscribe({
        next: data => {
          this.appointments = Array.isArray(data?.data) ? data.data : [];
          this.phase = 'pick';
        },
        error: () => {
          this.appointments = [];
          this.phase = 'pick';
        },
      });
  }

  // ── Calendar grid ─────────────────────────────────────────────────────────

  get monthTitle(): string {
    return this.currentMonth.toLocaleDateString(this.lang, { month: 'long', year: 'numeric' });
  }

  get calendarWeeks(): CalDay[][] {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    let startDow = firstOfMonth.getDay();
    startDow = (startDow + 6) % 7; // Monday-first

    const days: CalDay[] = [];

    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false, isPast: true, isToday: false });
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const isPast = date < todayMidnight;
      const isToday = date.getTime() === todayMidnight.getTime();
      days.push({ date, isCurrentMonth: true, isPast, isToday });
    }

    const trailing = (7 - (days.length % 7)) % 7;
    for (let d = 1; d <= trailing; d++) {
      days.push({ date: new Date(year, month + 1, d), isCurrentMonth: false, isPast: true, isToday: false });
    }

    const weeks: CalDay[][] = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
  }

  prevMonth(): void {
    const todayMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const prev = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    if (prev >= todayMonth) this.currentMonth = prev;
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
  }

  selectDate(date: Date): void {
    this.selectedDate = date;
    this.selectedSlot = null;
  }

  isSelectedDate(date: Date): boolean {
    if (!this.selectedDate) return false;
    return date.toDateString() === this.selectedDate.toDateString();
  }

  // ── Time slots ────────────────────────────────────────────────────────────

  get daySlots(): string[] {
    const slots: string[] = [];
    for (let h = SLOTS_START_H; h < SLOTS_END_H; h++) {
      for (const m of [0, 30]) {
        slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return slots;
  }

  get availableSlots(): string[] {
    if (!this.selectedDate) return [];
    const dateStr = this.toDateStr(this.selectedDate);
    const booked = this.getBookedRanges(dateStr);

    const slots: string[] = [];
    for (const slot of this.daySlots) {
      if (!this.isSlotBlocked(slot, booked)) {
        slots.push(slot);
      }
    }
    return slots;
  }

  isSlotOccupied(slot: string): boolean {
    if (!this.selectedDate) return false;
    return this.isSlotBlocked(slot, this.getBookedRanges(this.toDateStr(this.selectedDate)));
  }

  private getBookedRanges(dateStr: string): { start: number; end: number }[] {
    return this.appointments
      .filter(a => this.normalizeDateStr(a.appointment_date || a.date) === dateStr)
      .map(a => {
        const [h, m] = String(a.appointment_time || a.time || '00:00').slice(0, 5).split(':').map(Number);
        const start = (h || 0) * 60 + (m || 0);
        const dur = Number(a.duration_minutes || a.duration || SLOT_DURATION_MIN);
        return { start, end: start + dur };
      });
  }

  private isSlotBlocked(slot: string, bookedRanges: { start: number; end: number }[]): boolean {
    const [h, m] = slot.split(':').map(Number);
    const slotStart = (h || 0) * 60 + (m || 0);
    const slotEnd = slotStart + SLOT_DURATION_MIN;
    return bookedRanges.some(b => slotStart < b.end && slotEnd > b.start);
  }

  // Handles plain 'YYYY-MM-DD', ISO datetime strings, and Date objects.
  // Always returns local-time YYYY-MM-DD so it matches toDateStr().
  private normalizeDateStr(value: any): string {
    if (!value) return '';
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;   // already plain date
    const d = new Date(str);
    if (isNaN(d.getTime())) {
      return str.slice(0, 10);
    }
    // Use LOCAL date components — avoids UTC midnight shifting the date
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  selectSlot(slot: string): void {
    this.selectedSlot = slot;
    this.phase = 'details';
  }

  backToCalendar(): void {
    this.selectedSlot = null;
    this.formError = '';
    this.phase = 'pick';
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.formError = '';
    if (!this.name.trim()) { this.formError = this.translate.instant('BOOK_APPOINTMENT.ERROR_NAME_REQUIRED'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())) {
      this.formError = this.translate.instant('BOOK_APPOINTMENT.ERROR_EMAIL_INVALID');
      return;
    }

    this.isSubmitting = true;

    const dateStr = this.toDateStr(this.selectedDate!);
    const time = `${this.selectedSlot}:00`;
    const meetingUrl = buildSagelMeetingUrl({
      companyName: this.companyName,
      appointmentDate: dateStr,
      appointmentTime: this.selectedSlot || '',
      timezone: this.timezone,
      language: this.lang,
    });

    const payload = {
      user_id: Number(this.userId),
      title: this.name,
      customer_name: this.name,
      customer_email: this.email,
      customer_phone: this.phone || null,
      notes: this.notes || null,
      appointment_date: dateStr,
      appointment_time: time,
      duration_minutes: SLOT_DURATION_MIN,
      timezone: this.timezone,
      status: 'scheduled',
      meeting_url: meetingUrl,
      is_recurring: false,
    };

    this.http
      .post<any>(`${API_BASE}/public`, payload)
      .subscribe({
        next: res => {
          this.isSubmitting = false;
          this.phase = 'success';
          const created = res?.data ?? res;
          this.sendConfirmationEmail(
            created,
            resolveMeetingUrl(created?.meeting_url, meetingUrl),
            dateStr,
            time,
          );
        },
        error: _err => {
          this.isSubmitting = false;
          this.formError = this.translate.instant('BOOK_APPOINTMENT.ERROR_BOOKING_FAILED');
        },
      });
  }

  private sendConfirmationEmail(
    appointment: any,
    meetingUrl: string,
    dateStr: string,
    time: string,
  ): void {
    if (!this.email) return;

    const enc = encodeURIComponent(this.companyName);
    const id = appointment?.id ?? '';
    const rescheduleLink = id ? `${window.location.origin}/#/reschedule-general/${enc}/${id}` : '';

    const dateObj = new Date(`${dateStr}T00:00:00`);
    const longDate = dateObj.toLocaleDateString(this.lang, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const displayTime = time.slice(0, 5);

    const phoneRow = this.phone
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Phone</td><td style="padding:6px 0;color:#1f2a44;">${this.phone}</td></tr>`
      : '';
    const notesRow = this.notes
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Notes</td><td style="padding:6px 0;color:#1f2a44;">${this.notes}</td></tr>`
      : '';
    const meetingRow = meetingUrl
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Meeting</td><td style="padding:6px 0;color:#1f2a44;"><a href="${meetingUrl}" style="color:#1a3d8f;word-break:break-all;">${meetingUrl}</a></td></tr>`
      : '';
    const rescheduleRow = rescheduleLink
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Reschedule</td><td style="padding:6px 0;color:#1f2a44;"><a href="${rescheduleLink}" style="color:#1a3d8f;">Update this appointment</a></td></tr>`
      : '';

    const html = `
<div style="font-family:Arial,sans-serif;background:#f5f5f5;padding:24px 16px;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;margin:0 auto;background:#ffffff;border:1px solid #e0e0e0;border-radius:4px;">
    <tr>
      <td style="padding:24px 32px;border-bottom:2px solid #1a3d8f;">
        <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">${this.companyName}</div>
        <div style="font-size:20px;font-weight:600;color:#1a1a1a;">Appointment Confirmed</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <div style="font-size:13px;color:#888;margin-bottom:4px;">${longDate}</div>
        <div style="font-size:22px;font-weight:600;color:#1a1a1a;">${displayTime}</div>
        <div style="font-size:12px;color:#888;margin-top:4px;">${this.timezone}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <table width="100%" cellspacing="0" cellpadding="0" style="font-size:13px;color:#333;line-height:1.6;">
          <tr>
            <td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Name</td>
            <td style="padding:6px 0;color:#1f2a44;">${this.name}</td>
          </tr>
          <tr>
            <td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Email</td>
            <td style="padding:6px 0;color:#1f2a44;">${this.email}</td>
          </tr>
          ${phoneRow}
          ${notesRow}
          ${meetingRow}
          ${rescheduleRow}
        </table>
      </td>
    </tr>
    ${meetingUrl ? `
    <tr>
      <td style="padding:20px 32px;text-align:center;">
        <a href="${meetingUrl}" style="display:inline-block;padding:10px 22px;background:#1a3d8f;color:#fff;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;">Join Meeting</a>
      </td>
    </tr>` : ''}
  </table>
</div>`;

    this.http.post(`${environment.baseUrl}/email_notifications/info`, {
      header: `Appointment Confirmed - ${this.companyName}`,
      content: html,
      to_addr: this.email,
    }).subscribe();
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  get formattedSelectedDate(): string {
    if (!this.selectedDate) return '';
    return this.selectedDate.toLocaleDateString(this.lang, {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  }

  get companyInitial(): string {
    return this.companyName.charAt(0).toUpperCase() || 'S';
  }

  private toDateStr(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
